import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Filter, ChevronDown, Package, Upload } from 'lucide-react';
import { productsApi } from '../api/cms';
import { defaultProducts } from '../api/defaults';
import type { Product } from '../types';

const TYPE_LABELS: Record<string, string> = {
  hosting: 'Hosting', hardware: 'IT Hardware', webdesign: 'Web Design',
  callcenter: 'Call Center', voip: 'VoIP', crm: 'CRM', other: 'Altele',
};
const TYPE_COLORS: Record<string, string> = {
  hosting: 'bg-blue-500/20 text-blue-300',
  hardware: 'bg-purple-500/20 text-purple-300',
  webdesign: 'bg-green-500/20 text-green-300',
  callcenter: 'bg-yellow-500/20 text-yellow-300',
  voip: 'bg-orange-500/20 text-orange-300',
  crm: 'bg-pink-500/20 text-pink-300',
  other: 'bg-white/10 text-white/50',
};

function Select({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
        className="appearance-none bg-[#0d1117] border border-white/10 rounded-lg pl-3 pr-8 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors cursor-pointer [&>option]:bg-[#0d1117] [&>option]:text-white">
        {children}
      </select>
      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
    </div>
  );
}

const PAGE_SIZE = 20;

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    productsApi.list()
      .then(setProducts)
      .catch(() => setProducts(defaultProducts))
      .finally(() => setLoading(false));
  }, []);

  const del = async (id: string) => {
    if (!confirm('Ștergi produsul?')) return;
    await productsApi.delete(id).catch(() => {});
    setProducts(ps => ps.filter(p => p.id !== id));
  };

  // ── Derived filter options ─────────────────────────────────────────────────
  const categories = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach(p => { if (p.category) map.set(p.category, p.categoryLabel || p.category); });
    return Array.from(map.entries());
  }, [products]);

  const subcategories = useMemo(() => {
    const map = new Map<string, string>();
    products
      .filter(p => !filterCat || p.category === filterCat)
      .forEach(p => { if (p.subcategory) map.set(p.subcategory, p.subcategoryLabel || p.subcategory); });
    return Array.from(map.entries());
  }, [products, filterCat]);

  const suppliers = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => { if (p.supplier) set.add(p.supplier); });
    return Array.from(set).sort();
  }, [products]);

  // ── Filtered + paginated ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return products.filter(p => {
      const matchSearch = !q || p.name.toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      const matchCat = !filterCat || p.category === filterCat;
      const matchSub = !filterSub || p.subcategory === filterSub;
      const matchSupplier = !filterSupplier || p.supplier === filterSupplier;
      const matchStatus = !filterStatus || p.status === filterStatus;
      return matchSearch && matchCat && matchSub && matchSupplier && matchStatus;
    });
  }, [products, search, filterCat, filterSub, filterSupplier, filterStatus]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => { setSearch(''); setFilterCat(''); setFilterSub(''); setFilterSupplier(''); setFilterStatus(''); setPage(1); };
  const hasFilters = search || filterCat || filterSub || filterSupplier || filterStatus;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Produse & Servicii</h1>
          <p className="text-white/40 text-sm mt-1">{products.length} produse · {filtered.length} afișate</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/products/import"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#295CA8] text-white/60 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Upload size={15} /> Import CSV/XML/Feed
          </Link>
          <Link to="/products/new"
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16} /> Produs nou
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Caută după nume, brand, descriere..."
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
          </div>

          {/* Category */}
          <Select value={filterCat} onChange={v => { setFilterCat(v); setFilterSub(''); setPage(1); }}>
            <option value="">Toate categoriile</option>
            {categories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>

          {/* Subcategory */}
          <Select value={filterSub} onChange={v => { setFilterSub(v); setPage(1); }}>
            <option value="">Toate subcategoriile</option>
            {subcategories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>

          {/* Supplier */}
          <Select value={filterSupplier} onChange={v => { setFilterSupplier(v); setPage(1); }}>
            <option value="">Toți furnizorii</option>
            {suppliers.map(s => <option key={s} value={s}>{s.length > 35 ? s.slice(0,32)+'…' : s}</option>)}
          </Select>

          {/* Status */}
          <Select value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }}>
            <option value="">Orice status</option>
            <option value="active">Activ</option>
            <option value="inactive">Inactiv</option>
          </Select>

          {hasFilters && (
            <button onClick={resetFilters} className="text-xs text-[#4ab8ff] hover:underline whitespace-nowrap flex items-center gap-1">
              <Filter size={12} /> Reset filtre
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {hasFilters && (
          <div className="flex flex-wrap gap-2">
            {search && <span className="px-2 py-0.5 rounded-full bg-[#295CA8]/20 text-[#4ab8ff] text-xs">"{search}"</span>}
            {filterCat && <span className="px-2 py-0.5 rounded-full bg-[#295CA8]/20 text-[#4ab8ff] text-xs">{categories.find(c=>c[0]===filterCat)?.[1]}</span>}
            {filterSub && <span className="px-2 py-0.5 rounded-full bg-[#295CA8]/20 text-[#4ab8ff] text-xs">{subcategories.find(s=>s[0]===filterSub)?.[1]}</span>}
            {filterSupplier && <span className="px-2 py-0.5 rounded-full bg-[#295CA8]/20 text-[#4ab8ff] text-xs">{filterSupplier.slice(0,30)}</span>}
            {filterStatus && <span className="px-2 py-0.5 rounded-full bg-[#295CA8]/20 text-[#4ab8ff] text-xs">{filterStatus === 'active' ? 'Activ' : 'Inactiv'}</span>}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-white/30 text-sm">Se încarcă...</div>
        ) : paginated.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={40} className="mx-auto text-white/10 mb-3" />
            <p className="text-white/30 text-sm">Niciun produs găsit pentru filtrele selectate.</p>
            {hasFilters && <button onClick={resetFilters} className="mt-2 text-xs text-[#4ab8ff] hover:underline">Resetează filtrele</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">Produs</th>
                  <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden md:table-cell">Categorie</th>
                  <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">Preț vânz.</th>
                  <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden lg:table-cell">Achiz./Marjă</th>
                  <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden xl:table-cell">Furnizor</th>
                  <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {paginated.map(p => (
                  <tr key={p.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {p.image ? (
                          <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-contain bg-white/5 flex-shrink-0" onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                            <Package size={16} className="text-white/20" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm text-white font-medium leading-tight">{p.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {p.brand && <span className="text-xs text-white/30">{p.brand}</span>}
                            {p.badge && <span className="px-1.5 py-0 rounded text-[10px] font-semibold bg-[#4ab8ff]/15 text-[#4ab8ff]">{p.badge}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <div className="space-y-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[p.type] || TYPE_COLORS.other}`}>
                          {TYPE_LABELS[p.type] || p.type}
                        </span>
                        {p.subcategoryLabel && (
                          <div className="text-xs text-white/30">{p.subcategoryLabel}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-white">
                      <div className="font-semibold">{p.price} {p.currency}</div>
                      {p.priceTva && p.currency === 'RON' && (
                        <div className="text-xs text-white/30">{p.priceTva} RON c/TVA</div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      {p.purchasePrice != null ? (
                        <div className="text-xs space-y-0.5">
                          <div className="text-white/50">{p.purchasePrice} {p.currency} achiz.</div>
                          {p.margin != null && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                              p.margin >= 30 ? 'bg-green-500/20 text-green-400' :
                              p.margin >= 10 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                            }`}>{p.margin}% marjă</span>
                          )}
                        </div>
                      ) : <span className="text-white/20 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3.5 hidden xl:table-cell">
                      <span className="text-xs text-white/50 line-clamp-2 max-w-[180px]" title={p.supplier}>{p.supplier ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="space-y-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {p.status === 'active' ? 'Activ' : 'Inactiv'}
                        </span>
                        {p.inStock !== undefined && (
                          <div className={`text-xs ${p.inStock ? 'text-green-400/70' : 'text-red-400/70'}`}>
                            {p.inStock ? '✓ Stoc' : '✗ Stoc epuizat'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2 justify-end">
                        <Link to={`/products/${p.id}`}
                          className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all" title="Editează">
                          <Pencil size={14} />
                        </Link>
                        <button onClick={() => del(p.id)}
                          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Șterge">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-white/40">
            {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE, filtered.length)} din {filtered.length} produse
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
              className="px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30">← Anterior</button>
            {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
              let p = i + 1;
              if (totalPages > 7) {
                if (page <= 4) p = i + 1;
                else if (page >= totalPages - 3) p = totalPages - 6 + i;
                else p = page - 3 + i;
              }
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${p === page ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white hover:bg-white/10'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
              className="px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30">Următor →</button>
          </div>
        </div>
      )}
    </div>
  );
}
